import { IProductSearch } from 'shared/types/ProductSearch';
import { IProduct } from 'shared/types/Product';

export interface IWindowPosition {
  left: number;
  bottom: number;
}

interface ICategoryWindowPosition {
  [title: string]: IWindowPosition;
}

type IMiscData = { [key: string]: any };

export enum PDPStateActions {
  addWindow = 'addWindow',
  removeWindow = 'removeWindow',
  resetWindows = 'resetWindows',
  setInitialCategoryWindowPosition = 'setInitialCategoryWindowPosition',
  setBigBuyBarView = 'setBigBuyBarView',
  setMobileShouldAnimate = 'setMobileShouldAnimate',
  setMobileView = 'setMobileView',
  setSize = 'setSize',
  setWindowFocus = 'setWindowFocus',
  updateMisc = 'updateMisc',
  clearMisc = 'clearMisc',
}

export type BigBuyBarView = 'new' | 'used' | 'offer';
export type MobileView = 'main' | 'expanded' | 'expandedAuction' | 'detail';
export type PreviousMobileView = MobileView | 'none';

type IPDPStateAction =
  | { type: PDPStateActions.addWindow; payload: IProduct | IProductSearch }
  | { type: PDPStateActions.removeWindow; payload: IProduct | IProductSearch }
  | { type: PDPStateActions.resetWindows }
  | {
      type: PDPStateActions.setInitialCategoryWindowPosition;
      title: string;
      position: IWindowPosition;
    }
  | { type: PDPStateActions.setBigBuyBarView; view: BigBuyBarView }
  | { type: PDPStateActions.setMobileShouldAnimate; shouldAnimate: boolean }
  | { type: PDPStateActions.setMobileView; size: number; view: MobileView }
  | { type: PDPStateActions.setSize; size: number }
  | { type: PDPStateActions.setWindowFocus; id: string }
  | { type: PDPStateActions.updateMisc; key: string; data: IMiscData }
  | { type: PDPStateActions.clearMisc; key: string };

export type IPDPStateDispatch = (action: IPDPStateAction) => void;

export interface IPDPWindowFromProduct extends IProduct {
  positionOffset: number;
}

export interface IPDPWindowFromSearch extends IProductSearch {
  positionOffset: number;
}

export type IPDPWindow = IPDPWindowFromProduct | IPDPWindowFromSearch;

export interface IPDPState {
  focusedWindow: string;
  initialCategoryWindowPosition: ICategoryWindowPosition;
  bigBuyBarView: BigBuyBarView;
  mobileView: MobileView;
  previousMobileView: PreviousMobileView;
  mobileShouldAnimate: boolean;
  openWindows: IPDPWindow[];
  positionOffset: number;
  size: number;
  misc: IMiscData;
}

export const INITIAL_PDP_STATE: IPDPState = {
  focusedWindow: null,
  initialCategoryWindowPosition: null,
  bigBuyBarView: 'new',
  mobileView: 'main',
  previousMobileView: 'none',
  mobileShouldAnimate: true,
  openWindows: [],
  positionOffset: 0,
  size: null,
  misc: {},
};

export const pdpStateReducer = (state: IPDPState, action: IPDPStateAction) => {
  switch (action.type) {
    case PDPStateActions.addWindow: {
      const { payload } = action;
      const newWindow = { ...payload, ...{ positionOffset: state.positionOffset } };

      if (!state.openWindows.some((window) => window.id === payload.id)) {
        state.openWindows = [...state.openWindows, newWindow];
        state.positionOffset += 24;
      }
      break;
    }

    case PDPStateActions.removeWindow: {
      const { payload } = action;
      state.openWindows = state.openWindows.filter((window) => window.id !== payload.id);
      state.positionOffset -= 24;
      break;
    }

    case PDPStateActions.resetWindows: {
      state = { ...INITIAL_PDP_STATE, size: state.size };
      break;
    }

    case PDPStateActions.setInitialCategoryWindowPosition: {
      const { title, position } = action;
      state.initialCategoryWindowPosition = {
        ...state.initialCategoryWindowPosition,
        ...{ [title]: position },
      };
      break;
    }

    case PDPStateActions.setBigBuyBarView:
      state.bigBuyBarView = action.view;
      break;

    case PDPStateActions.setMobileView:
      state.previousMobileView = state.mobileView;
      state.mobileView = action.view;
      state.size = action.size;
      break;

    case PDPStateActions.setMobileShouldAnimate:
      state.mobileShouldAnimate = action.shouldAnimate;
      break;

    case PDPStateActions.setWindowFocus:
      state.focusedWindow = action.id;
      break;

    case PDPStateActions.setSize:
      state.size = action.size;
      break;

    case PDPStateActions.updateMisc:
      state.misc = {
        ...state.misc,
        [action.key]: {
          ...state.misc[action.key],
          ...action.data,
        },
      };
      break;

    case PDPStateActions.clearMisc: {
      const newMisc = { ...state.misc };
      delete newMisc[action.key];
      state.misc = newMisc;
      break;
    }

    default:
      return state;
  }

  return state;
};
